import { useEffect, useRef } from 'react';
import { reaction } from 'mobx';
import { dismissToasts, toast, updateToast } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { tableStore } from '../../stores/table-store';
import { fetchVersionFileContent } from '../../utils/fetch-version-file-content';
import { uploadFile } from '../../utils/upload-file';
import type { ProStudioContext } from '../../xstate/prostudio.xstate.types';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { sendUserMessage } from '../../xstate/app.xstate-utils';
import {
  hasPendingKnowledgeSetup,
  takePendingKnowledgeSetup,
} from './knowledge-keys';
import { reportSetupOutcome, type Translate } from './knowledge-setup-text';
import {
  adaptUploadFile,
  createKnowledgeSetupDeps,
} from './knowledge-setup-deps';
import { postKnowledge } from './post-knowledge';
import { handleKnowledgeSetupError } from './report-knowledge-failure';
import { runKnowledgeSetup } from './run-knowledge-setup';

/**
 * Runs Talk it through's pending knowledge setup (`knowledge-keys.ts`) once
 * the solution it created is open: its draft version known and its rows,
 * search node included, loaded. Mounted once, at the app root.
 */
export const useKnowledgeSetup = () => {
  const { uniqueId, projectId, versionId } = useProStudioCtxSelector(
    (ctx: ProStudioContext) => ({
      uniqueId: ctx.selectedBot?.uniqueId,
      projectId: ctx.project?.id,
      versionId:
        ctx.customerId && ctx.botName && ctx.draftVersion
          ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
          : undefined,
    }),
    { isEqualityDeep: true }
  );
  const running = useRef(false);
  // The run outlives a render: it asks which solution is open NOW, and
  // compares it with the one it started in.
  const openSolutionRef = useRef(uniqueId);
  openSolutionRef.current = uniqueId;
  const [t] = useTranslation();
  // The run outlives a render: it reads the latest `t` without restarting the
  // effect (which would re-subscribe the rows reaction) on a language change.
  const tRef = useRef(t);
  tRef.current = t;
  const translate: Translate = (key, options) =>
    String(tRef.current(key, options as never));

  useEffect(() => {
    if (!uniqueId || !projectId || !versionId) return undefined;
    if (!hasPendingKnowledgeSetup(uniqueId) || running.current) {
      return undefined;
    }

    const start = () => {
      if (running.current || !tableStore.rows.length) return;
      const setup = takePendingKnowledgeSetup(uniqueId);
      if (!setup) return;
      running.current = true;

      const outcomeDeps = {
        t: translate,
        send: sendUserMessage,
        dismiss: dismissToasts,
        solutionKey: uniqueId,
      };

      runKnowledgeSetup(
        setup,
        createKnowledgeSetupDeps({
          solutionKey: uniqueId,
          readOpenSolutionKey: () => openSolutionRef.current,
          readStoreSolutionVersionId: () => tableStore.solutionVersionId,
          readRows: () => tableStore.rows,
          updateRow: (args) => tableStore.updateRow(args),
          upload: adaptUploadFile(uploadFile),
          uploadUrl: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/scripts`,
          dispatchDraft: (draft) =>
            sendProStudioEvent({
              type: 'updateDraftData',
              draft: draft as ProStudioContext['draft'],
              action: 'upload',
            }),
          readScript: (path) =>
            fetchVersionFileContent({ projectId, versionId, filePath: path }),
          post: postKnowledge,
          t: translate,
          isToastActive: (id) => toast.isActive(id),
          sendToast: sendUserMessage,
          updateToast,
        })
      )
        .then((result) => reportSetupOutcome({ result }, outcomeDeps))
        .catch((err: unknown) => handleKnowledgeSetupError(err, outcomeDeps))
        .finally(() => {
          running.current = false;
        });
    };

    start();
    // Rows land after the version: wait for them.
    return reaction(
      () => tableStore.rows.length,
      () => start()
    );
  }, [uniqueId, projectId, versionId]);
};
